import { ApiResponse } from '@models/api-response';
import { UserError } from '@models/error-handling/user-error.model';
import {
  IPagedResponse,
  PagedResponse
} from '@models/paged-response';
import { StandardHttpResponse } from '@models/standard-http-response';
import {
  apiResponseFromArray,
  apiResponseFromJson, apiResponseFromJsonInterface,
  pagedResponseFromArray,
  pagedResponseFromArrayInterface,
} from '@shared/zb-object-helper/zb-object-helper-functions';
import { Observable, of, OperatorFunction, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export type Type<T> = new (...args: T[]) => T;

export function instantiateApiResponseFromJson<T>(classCast: Type<T>) {
  return (source$: Observable<unknown>): Observable<ApiResponse<T>> => source$.pipe(
    map(res => apiResponseFromJson(classCast, res as StandardHttpResponse)),
  );
}

export function instantiateApiResponseFromJsonInterface<T>() {
  return (source$: Observable<unknown>): Observable<ApiResponse<T | null>> => source$.pipe(
    map(res => apiResponseFromJsonInterface<T>(res as StandardHttpResponse)),
  );
}

export function instantiateApiResponseFromArray<T>(classCast: Type<T>) {
  return (source$: Observable<unknown>): Observable<ApiResponse<T[]>> => source$.pipe(
    map(res => apiResponseFromArray(classCast, res as StandardHttpResponse)),
  );
}

/**
 * Creates a PagedResponse from a JSON array.
 * @param classCast Constructor function for response type.
 */
export function instantiatePagedResponseFromArray<T>(classCast: Type<T>) {
  return (source$: Observable<unknown>): Observable<IPagedResponse<T[]>> => source$.pipe(
    map(res => pagedResponseFromArray(classCast, res as StandardHttpResponse)),
  );
}

/**
 * Creates a PagedResponse from a JSON array, trusting that the
 * response data matches the interface type T.
 */
export function instantiatePagedResponseFromArrayInterface<T>() {
  return (source$: Observable<unknown>): Observable<PagedResponse<T[]>> => source$.pipe(
    map(res => pagedResponseFromArrayInterface<T>(res as StandardHttpResponse)),
  );
}

export function catchAndOverrideErrorHandlingMessages<T>(userMessages: string[]): OperatorFunction<T, T> {
  return (source$: Observable<T>) => source$.pipe(
    catchError(() => throwError(() => new UserError(userMessages))),
  );
}

export function addIncrementer<T>(): (source: Observable<T>) => Observable<[number, T]> {
  let counter = 0;
  return (source$: Observable<T>) => source$.pipe(
    map((value) => {
      counter++;
      return [counter, value];
    })
  );
}

/**
 * Commonly used to catch NgbModal dismiss results, which are promise rejects, which RxJS will interpret as
 * errors.
 *
 * This simply takes the non-error (not an instance of {@link Error}) and outputs it as part of the
 * main observable flow, to be handled.
 *
 * Otherwise, the err value is re-thrown so that it can bubble up to ErrorHandlingService.
 */
export function catchAndRedirectNonErrorToMainFlow() {
  return catchError((err: any) => {
    if (err instanceof Error) {
      throw err;
    }
    return of(err);
  });
}
